import { Stack, useNavigation } from 'expo-router';
import { ChevronLeft } from 'lucide-react-native';
import { isValidElement, useEffect } from 'react';
import { twJoin } from 'tailwind-merge';

import type { HeaderProps } from './header.types';
import { Button, ButtonIcon, ButtonText } from './ui/button';
import { Heading } from './ui/heading';
import { HStack } from './ui/hstack';
import { Text } from './ui/text';

import { HeaderItemRow } from '@/components/headerItems/HeaderItemRow';
import { hasVisibleHeaderItems } from '@/components/headerItems/hasVisibleHeaderItems';
import { ROUTES } from '@/constants/routes';
import { useBackNavigation } from '@/hooks/useBackNavigation';

export type { HeaderProps } from './header.types';

/**
 * Header Component (Web)
 *
 * Web-specific header with three-column responsive layout (left | title | right).
 * Disables React Navigation's native header and renders web-appropriate chrome.
 * Supports back navigation via `backRoute` and custom left/right actions. A slot given
 * `leftItems` or `rightItems` draws those and ignores its render function, as on native.
 * `fill="brand"` turns the items light for a row that sits on a brand-colored screen; the
 * screen paints the fill itself, since this row has no background of its own.
 */
export function Header({
  title,
  subtitle,
  isBackButton = false,
  backButtonText = 'Back',
  isLarge = false,
  backRoute,
  backRouteParams,
  headerShown = true,
  headerLeft,
  headerRight,
  headerTitle,
  headerShadowVisible,
  className,
  leftItems,
  rightItems,
  fill = 'default',
}: HeaderProps) {
  const navigation = useNavigation();

  const { navigateBack } = useBackNavigation(backRoute ?? ROUTES.COMMUNITY, backRouteParams);

  useEffect(() => {
    navigation.setOptions({
      headerShown: false,
    });
  }, [navigation]);

  if (!headerShown) {
    return <Stack.Screen options={{ headerShown: false }} />;
  }

  const hasContent =
    Boolean(title) ||
    Boolean(headerTitle) ||
    isBackButton ||
    Boolean(headerLeft) ||
    Boolean(headerRight) ||
    hasVisibleHeaderItems(leftItems) ||
    hasVisibleHeaderItems(rightItems);

  if (!hasContent) {
    return <Stack.Screen options={{ headerShown: false }} />;
  }

  const itemTone = fill === 'brand' ? 'onBrand' : 'default';

  const backButton =
    isBackButton && backRoute ? (
      <Button
        variant="link"
        size="md"
        action="primary"
        onPress={navigateBack}
        className="flex-row items-center gap-2">
        <ButtonIcon as={ChevronLeft} className="h-6 w-6" />
        <ButtonText>{backButtonText}</ButtonText>
      </Button>
    ) : null;

  const renderLeft = () => {
    if (backButton) {
      return <HStack className="items-center">{backButton}</HStack>;
    }

    if (leftItems && leftItems.length > 0) {
      return <HeaderItemRow items={leftItems} tone={itemTone} />;
    }

    if (typeof headerLeft === 'function') {
      return (
        <HStack space="md" className="items-center">
          {headerLeft({ tintColor: '', canGoBack: false })}
        </HStack>
      );
    }

    if (isValidElement(headerLeft)) {
      return (
        <HStack space="md" className="items-center">
          {headerLeft}
        </HStack>
      );
    }

    return null;
  };

  const renderTitle = () => {
    if (typeof headerTitle === 'function') {
      return headerTitle({ tintColor: '', children: typeof title === 'string' ? title : '' });
    }

    if (isValidElement(headerTitle)) {
      return headerTitle;
    }

    if (isValidElement(title)) {
      return title;
    }

    if (title) {
      const headingSize = isLarge ? 'h1-2xl' : 'h1-lg';

      return (
        <Heading className="truncate text-center" size={headingSize}>
          {title}
        </Heading>
      );
    }

    return null;
  };

  const renderRight = () => {
    if (rightItems && rightItems.length > 0) {
      return <HeaderItemRow items={rightItems} tone={itemTone} />;
    }

    if (typeof headerRight === 'function') {
      return headerRight({ tintColor: '', canGoBack: false });
    }

    if (isValidElement(headerRight)) {
      return headerRight;
    }

    return null;
  };

  const titleContent = renderTitle();

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <HStack
        className={twJoin(
          'min-h-16 w-full items-center justify-center',
          headerShadowVisible && 'border-b border-outline-200',
          className
        )}>
        <HStack className="mx-auto w-full max-w-5xl items-center px-4 md:px-6">
          <HStack className="min-w-0 flex-1 items-center justify-start">{renderLeft()}</HStack>

          {titleContent && (
            <HStack className="min-w-0 flex-1 items-center justify-center overflow-hidden px-2">
              {titleContent}
            </HStack>
          )}

          <HStack className="min-w-0 flex-1 items-center justify-end">{renderRight()}</HStack>
        </HStack>
      </HStack>
      {subtitle &&
        (typeof subtitle === 'string' ? <Text className="px-5 pb-2">{subtitle}</Text> : subtitle)}
    </>
  );
}
